import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';

import RowList from '@rover/components/src/RowList';
import { MAX_ALLOWED_PETS } from '@rover/constants/src/search/searchPage.constants';
import { Text } from '@rover/kibble/core';
import Row from '@rover/kibble/patterns/Row';
import { StyledStepper } from '@rover/react-lib/src/components/forms/AddPetForm/AddPetFormFlow/styles';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { PetType } from '@rover/types/src/Pet';

import type { TriggeredFrom } from './analyticsEvents';
import { PetCountAddedEvent, PetCountRemovedEvent } from './analyticsEvents';
import PetCounterIcon from './PetCounterIcon';

type PetTypeDetails = {
  icon: React.JSX.Element;
  label: string;
  singularNoun: string;
  value: number;
  maxValue: number;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  sublabel?: string;
};

type PetTypeDetailsMap = {
  [petType in PetType]: PetTypeDetails;
};

export type PetCounterIconVariant = 'default' | 'color';

export type PetCounterBodyProps = {
  dogs: number;
  cats: number;
  puppies: number;
  availablePets: string[]; // Change to PetType[] in DEV-106783
  triggeredFrom?: TriggeredFrom;
  setDogs: React.Dispatch<React.SetStateAction<number>>;
  setPuppies: React.Dispatch<React.SetStateAction<number>>;
  setCats: React.Dispatch<React.SetStateAction<number>>;
  iconVariant?: PetCounterIconVariant;
};

const PetCounterBody: FC<PetCounterBodyProps> = ({
  dogs,
  cats,
  puppies,
  availablePets,
  triggeredFrom,
  setDogs,
  setPuppies,
  setCats,
  iconVariant = 'default',
}) => {
  const { t } = useLingui();
  const increaseButtonRef = useRef<Record<string, HTMLButtonElement>>(null);
  const petTypeMap: PetTypeDetailsMap = useMemo(
    () => ({
      [PetType.DOG]: {
        icon: <PetCounterIcon petType={PetType.DOG} variant={iconVariant} />,
        label: t`Dogs`,
        singularNoun: t`dog`,
        value: dogs,
        maxValue: MAX_ALLOWED_PETS - cats - puppies,
        setValue: setDogs,
      },
      [PetType.PUPPY]: {
        icon: <PetCounterIcon petType={PetType.PUPPY} variant={iconVariant} />,
        label: t`Puppies`,
        singularNoun: t`puppy`,
        value: puppies,
        maxValue: MAX_ALLOWED_PETS - dogs - cats,
        setValue: setPuppies,
        sublabel: t`Younger than 1 year old`,
      },
      [PetType.CAT]: {
        icon: <PetCounterIcon petType={PetType.CAT} variant={iconVariant} />,
        label: t`Cats`,
        singularNoun: t`cat`,
        value: cats,
        maxValue: MAX_ALLOWED_PETS - dogs - puppies,
        setValue: setCats,
      },
    }),
    [dogs, cats, puppies, setDogs, setCats, setPuppies, iconVariant, t]
  );

  const handleCounterIncrement = useCallback(
    (pet: string): void => {
      emitAnalyticsEvent(new PetCountAddedEvent({ triggeredFrom }));
      petTypeMap[pet].setValue((prev: number) => prev + 1);
    },
    [petTypeMap, triggeredFrom]
  );

  const handleCounterDecrement = useCallback(
    (pet: string): void => {
      emitAnalyticsEvent(new PetCountRemovedEvent({ triggeredFrom }));
      if (petTypeMap[pet].value === 1) {
        increaseButtonRef[pet].focus();
      }
      petTypeMap[pet].setValue((prev: number) => prev - 1);
    },
    [petTypeMap, triggeredFrom]
  );

  return (
    <div data-testid="pet-counter-body">
      <RowList>
        {availablePets?.map((pet) => (
          <Row
            key={pet}
            label={
              <Text size="200" fontWeight="semibold">
                {petTypeMap[pet].label}
              </Text>
            }
            leftItem={petTypeMap[pet].icon}
            {...(petTypeMap[pet].sublabel ? { sublabel: petTypeMap[pet].sublabel } : {})}
            rightItem={
              <StyledStepper
                value={petTypeMap[pet].value}
                onIncrement={() => handleCounterIncrement(pet)}
                onDecrement={() => handleCounterDecrement(pet)}
                minValue={0}
                maxValue={petTypeMap[pet].maxValue}
                focusableRef={(ref) => {
                  increaseButtonRef[pet] = ref;
                }}
                incrementProps={{
                  'aria-label': t({
                    message: `Add one ${petTypeMap[pet].singularNoun}, current value ${petTypeMap[pet].value}`,
                    comment:
                      'This is an aria-label on a plus button where users indicate how many dogs they have. The label announces currently selected value, e.g. "Add one dog, current value 3"',
                  }),
                }}
                decrementProps={{
                  'aria-label': t({
                    message: `Remove one ${petTypeMap[pet].singularNoun}, current value ${petTypeMap[pet].value}`,
                    comment:
                      'This is an aria-label on a minus button where users indicate how many dogs they have. The label announces currently selected value, e.g. "Remove one dog, current value 3"',
                  }),
                }}
                inputProps={{
                  'aria-roledescription': petTypeMap[pet].label,
                  'aria-disabled': true,
                }}
              />
            }
          />
        ))}
      </RowList>
    </div>
  );
};
export default PetCounterBody;
